import React from "react"
import { GenericResource } from "models/generic"
import { UseInfiniteQueryGenericProps } from "hooks/useInfiniteQueryGeneric"
import { Button, Dropdown, Menu } from "antd"
import SearchEmpty from "./SearchEmpty"
import Empty from "./Empty"
import "./table.scss"
import MenuLink from "components-old/menu/MenuLink"
import SubMenu from "antd/lib/menu/SubMenu"
import { exportTableData } from "./table.helper"
import { Csv02Icon, Download02Icon, Xls02Icon } from "components-old/icons"
import { DnDInfiniteTable } from "./dnd/DnDInfiniteTable"
import VirtualInfiniteTable, { VirtualInfiniteTableProps } from "./virtual/VirtualInfiniteTable"
import CardLayout from "../page/CardLayout"
import Separator from "components/navigation/Separator"
import { IS_AGENT_APP, isAldeia } from "hooks/useAldeia"
import { cn } from "lib/utils"
import { Link } from "react-router-dom"
import GridLayout from "./grid/GridTable"
import GridTable from "./grid/GridTable"
import { ColumnDef } from "@tanstack/react-table"

export interface TableExtraMore {
  id: string
  title?: string
  iconName?: string
  to?: string
  onClick?: () => void
  custom?: React.ReactNode
  disabled?: boolean
  separator?: boolean
}

export interface TablePageProps<T extends GenericResource> {
  title?: React.ReactNode
  initialData?: UseInfiniteQueryGenericProps<T>["initialData"]
  onSuccess?: UseInfiniteQueryGenericProps<T>["onSuccess"]
  top?: React.ReactNode
  getExportableData?: (data: T[]) => Promise<{ exportableData: Record<string, string>[]; filename: string }>
  extra?: TableExtraMore[]
  actions?: React.ReactNode
  dnd?: boolean
  cardLayoutClassName?: string
  children?: React.ReactNode
  before?: React.ReactNode
  sideCollapsible?: boolean
  settingsKey?: string
  layout?: "static" | "default" | "grid"
  rowHeight?: number
  emptyComponent?: React.ReactNode
  gridElement?: React.ComponentType<{ item: T }>
  tableActionsColumn?: ColumnDef<T>
  routeBase?: string
}

export function TablePage<T extends GenericResource>(props: TablePageProps<T> & { tableProps: VirtualInfiniteTableProps<T> }) {
  // const setSidebarCollapsed = useSetAtom(sidebarCollapsedAtom)

  const isStatic = props.layout === "static"

  const hasExtra = !!props.extra?.length || !!props.getExportableData
  const showNoData = !props.tableProps?.isFetching && props.tableProps?.data.length === 0

  const top = (
    <div
      id="stuckable"
      className={cn(
        "px-4 aldeia:px-0 aldeia:pt-4",
        showNoData && !isStatic && "aldeia:px-4",
        isStatic && "aldeia:pt-0 z-[1] relative",
        IS_AGENT_APP && "sticky top-0 z-10 bg-base-200",
      )}
    >
      {props.top}
    </div>
  )

  return (
    <CardLayout
      layout={props.layout}
      // className="pb-4 mx-5 mt-5 mb-20 w-full max-w-3xl mobile:shadow-none mobile:max-w-full mobile:p-0 mobile:m-0"
      // headerClassName="mobile:hidden"
      title={
        // isAldeia && props.sideCollapsible ? (
        //   <div className="flex items-center gap-1.5">
        //     <Button
        //       // icon={<IconMageAlignLeft className="[&_path]:stroke-2" />}
        //       icon={<IconMageFolder2 className="[&_path]:stroke-2 !size-[20px]" />}
        //       type="text"
        //       className="bg-blue-500/10 text-blue-500 size-[32px]"
        //       onClick={() => {
        //         setSidebarCollapsed((s) => !s)
        //       }}
        //     ></Button>
        //     {props.title}
        //   </div>
        // ) : (
        props.title
        // )
      }
      extra={
        <div className="flex gap-2 items-center">
          {isStatic && props.actions}
          {hasExtra ? (
            <Dropdown
              key={"drop"}
              placement="bottomRight"
              overlay={
                <Menu>
                  {/* <Menu expandIcon={}> */}
                  {props.extra?.map((e) => {
                    if (e.custom) {
                      return e.custom
                    }
                    if (e.separator) {
                      return <Separator key={e.id || "separator-menu"} />
                    }
                    if (e.to) {
                      return (
                        <Menu.Item key={e.id} disabled={e.disabled}>
                          <MenuLink iconName={e.iconName} to={e.to}>
                            {e.title}
                          </MenuLink>
                        </Menu.Item>
                      )
                    }
                    return (
                      <Menu.Item key={e.id} icon={<i className={e.iconName} />} disabled={e.disabled} onClick={e.onClick}>
                        {e.title}
                      </Menu.Item>
                    )
                  })}
                  {!!props.getExportableData && (
                    <SubMenu
                      key="export"
                      icon={
                        <i>
                          <Download02Icon />
                        </i>
                      }
                      title="Exportar"
                    >
                      <Menu.Item
                        key="export-csv"
                        icon={
                          <i>
                            <Csv02Icon />
                          </i>
                        }
                        onClick={() => {
                          exportTableData(props.tableProps.data, props.getExportableData!)
                        }}
                      >
                        CSV
                      </Menu.Item>
                      <Menu.Item
                        key="export-xlsx"
                        icon={
                          <i>
                            <Xls02Icon />
                          </i>
                        }
                        onClick={async () => {
                          exportTableData(props.tableProps.data, props.getExportableData!, { xls: true })
                        }}
                      >
                        Excel
                      </Menu.Item>
                    </SubMenu>
                  )}
                </Menu>
              }
              trigger={["click"]}
            >
              <Button
                type={isStatic ? "text" : "text"}
                className={cn("rounded-xl h-8 btn-white", isStatic && "size-8")}
                icon={<i className="far fa-ellipsis" />}
              />
            </Dropdown>
          ) : undefined}
        </div>
      }
      className={props.cardLayoutClassName}
      contentClassName="h-full"
    >
      {props.top && (!isAldeia || showNoData || isStatic || props.layout === "grid") && top}
      {showNoData && isStatic && props.before}

      {showNoData ? (
        <div className="flex flex-col justify-center items-center pt-12 pb-24">
          {props.tableProps?.isFiltering ? (
            <>
              <SearchEmpty className="w-52 h-52" />
              <span className="-mt-2 text-content-200">Não encontramos nenhum registro com esses filtros...</span>
            </>
          ) : (
            <>
              {props.emptyComponent || (
                <>
                  <Empty className="w-52 h-52" />
                  <span className="-mt-2">Parece que você ainda não adicionou nenhum registro...</span>
                </>
              )}
            </>
          )}
        </div>
      ) : props.dnd ? (
        <DnDInfiniteTable<T>
          className={cn("px-2 aldeia:px-4 mobile:px-0", isStatic && "aldeia:px-0")}
          {...props.tableProps}
          layout={props.layout}
        >
          {props.top && !isStatic && isAldeia && top}
          {props.before}
          {props.children}
        </DnDInfiniteTable>
      ) : props.layout === "grid" ? (
        <GridTable
          {...props.tableProps}
          gridElement={props.gridElement}
          tableActionsColumn={props.tableActionsColumn}
          routeBase={props.routeBase}
        />
      ) : (
        <VirtualInfiniteTable<T>
          className={cn("px-2 aldeia:px-4 mobile:px-0", isStatic && "aldeia:px-0")}
          {...props.tableProps}
          layout={props.layout}
        >
          {props.top && isAldeia && !isStatic && top}
          {props.children}
          {props.before}
        </VirtualInfiniteTable>
      )}
    </CardLayout>
  )
}
